/**
 * Layer File Types Configuration
 *
 * Defines permitted file types for each layer based on architecture documentation.
 * Each layer has specific requirements for asset types.
 */

import { LayerType } from '../../../types/layerMetadata';

export interface FileTypeConfig {
  accept: string;
  extensions: string[];
  mimeTypes: string[];
  maxSizeMB: number;
  description: string;
  allowMultiple: boolean;
}

/**
 * Layer-specific file type configurations
 * Based on architecture specifications:
 * - Songs (G): Audio files only
 * - Stars (S): Images only
 * - Looks (L): Images only
 * - Moves (M): Videos only
 * - Worlds (W): Videos only
 * - Composites (C): Videos only (but selects existing components)
 * - Training Data (T): Any file type (datasets)
 * - Rights (R): Documents and metadata
 * - Personalize (P): User uploads (images/videos)
 * - Branded (B): Images or Videos (brand content)
 */
export const LAYER_FILE_TYPES: Record<LayerType, FileTypeConfig> = {
  G: {
    // Songs Layer - Audio only
    accept: 'audio/*',
    extensions: ['.mp3', '.wav', '.m4a', '.aac', '.flac', '.ogg'],
    mimeTypes: [
      'audio/mpeg',
      'audio/wav',
      'audio/x-wav',
      'audio/mp4',
      'audio/aac',
      'audio/flac',
      'audio/ogg',
    ],
    maxSizeMB: 32,
    description: 'Audio files (MP3, WAV, M4A, AAC, FLAC, OGG)',
    allowMultiple: false,
  },

  S: {
    // Stars Layer - Images only
    accept: 'image/*',
    extensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'],
    mimeTypes: [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/svg+xml',
    ],
    maxSizeMB: 32,
    description: 'Image files (JPEG, PNG, GIF, WebP, SVG)',
    allowMultiple: true,
  },

  L: {
    // Looks Layer - Images only
    accept: 'image/*',
    extensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'],
    mimeTypes: [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/svg+xml',
    ],
    maxSizeMB: 32,
    description: 'Image files (JPEG, PNG, GIF, WebP, SVG)',
    allowMultiple: true,
  },

  M: {
    // Moves Layer - Videos only
    accept: 'video/*',
    extensions: ['.mp4', '.mov', '.avi', '.webm', '.mkv'],
    mimeTypes: [
      'video/mp4',
      'video/quicktime',
      'video/x-msvideo',
      'video/webm',
      'video/x-matroska',
    ],
    maxSizeMB: 32,
    description: 'Video files (MP4, MOV, AVI, WebM, MKV)',
    allowMultiple: true,
  },

  W: {
    // Worlds Layer - Videos only
    accept: 'video/*',
    extensions: ['.mp4', '.mov', '.avi', '.webm', '.mkv'],
    mimeTypes: [
      'video/mp4',
      'video/quicktime',
      'video/x-msvideo',
      'video/webm',
      'video/x-matroska',
    ],
    maxSizeMB: 32,
    description: 'Video files (MP4, MOV, AVI, WebM, MKV)',
    allowMultiple: true,
  },

  C: {
    // Composite Layer - Videos only
    accept: 'video/*',
    extensions: ['.mp4', '.mov', '.avi', '.webm', '.mkv'],
    mimeTypes: [
      'video/mp4',
      'video/quicktime',
      'video/x-msvideo',
      'video/webm',
      'video/x-matroska',
    ],
    maxSizeMB: 32,
    description: 'Video files (MP4, MOV, AVI, WebM, MKV)',
    allowMultiple: false,
  },

  T: {
    // Training Data Layer - Various formats
    accept: '*',
    extensions: ['.json', '.csv', '.txt', '.zip', '.tar', '.gz'],
    mimeTypes: [
      'application/json',
      'text/csv',
      'text/plain',
      'application/zip',
      'application/x-tar',
      'application/gzip',
    ],
    maxSizeMB: 32,
    description: 'Training data files (JSON, CSV, TXT, ZIP)',
    allowMultiple: true,
  },

  R: {
    // Rights Layer - Documents
    accept: '.pdf,.doc,.docx,.txt,.json',
    extensions: ['.pdf', '.doc', '.docx', '.txt', '.json'],
    mimeTypes: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'application/json',
    ],
    maxSizeMB: 32,
    description: 'Rights documents (PDF, DOC, TXT)',
    allowMultiple: true,
  },

  P: {
    // Personalize Layer - User content
    accept: 'image/*,video/*',
    extensions: [
      '.jpg',
      '.jpeg',
      '.png',
      '.gif',
      '.webp',
      '.mp4',
      '.mov',
      '.avi',
      '.webm',
      '.mkv',
    ],
    mimeTypes: [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'video/mp4',
      'video/quicktime',
      'video/x-msvideo',
      'video/webm',
      'video/x-matroska',
    ],
    maxSizeMB: 32,
    description: 'User-uploaded images or videos',
    allowMultiple: true,
  },

  B: {
    // Branded Layer - Images or Videos
    accept: 'image/*,video/*',
    extensions: [
      '.jpg',
      '.jpeg',
      '.png',
      '.gif',
      '.webp',
      '.mp4',
      '.mov',
      '.avi',
      '.webm',
      '.mkv',
    ],
    mimeTypes: [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'video/mp4',
      'video/quicktime',
      'video/x-msvideo',
      'video/webm',
      'video/x-matroska',
    ],
    maxSizeMB: 32,
    description: 'Brand content images or videos',
    allowMultiple: true,
  },
};

/**
 * Validate if a file is acceptable for a specific layer
 */
export function validateFileForLayer(
  file: File,
  layerType: LayerType
): { valid: boolean; error?: string } {
  const config = LAYER_FILE_TYPES[layerType];

  if (!config) {
    return { valid: false, error: `Unknown layer type: ${layerType}` };
  }

  // Check MIME type
  if (!config.mimeTypes.includes(file.type)) {
    // Also check by extension as fallback
    const fileName = file.name.toLowerCase();
    const hasValidExtension = config.extensions.some(ext =>
      fileName.endsWith(ext)
    );

    if (!hasValidExtension) {
      return {
        valid: false,
        error: `Invalid file type. ${config.description} required.`,
      };
    }
  }

  // Check file size
  const sizeMB = file.size / (1024 * 1024);
  if (sizeMB > config.maxSizeMB) {
    return {
      valid: false,
      error: `File too large. Maximum size is ${config.maxSizeMB}MB.`,
    };
  }

  return { valid: true };
}

/**
 * Get human-readable file type description for a layer
 */
export function getLayerFileTypeDescription(layerType: LayerType): string {
  const config = LAYER_FILE_TYPES[layerType];
  return config ? config.description : 'Unknown file type';
}

/**
 * Get accept attribute for file input
 */
export function getLayerAcceptAttribute(layerType: LayerType): string {
  const config = LAYER_FILE_TYPES[layerType];
  return config ? config.accept : '*';
}

/**
 * Check if a layer supports multiple files
 */
export function layerSupportsMultipleFiles(layerType: LayerType): boolean {
  const config = LAYER_FILE_TYPES[layerType];
  return config ? config.allowMultiple : false;
}

/**
 * Categorize file type (audio, image, video)
 */
export function getFileCategory(
  file: File
): 'audio' | 'image' | 'video' | 'unknown' {
  const type = file.type.toLowerCase();

  if (type.startsWith('audio/')) return 'audio';
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';

  // Check by extension as fallback
  const ext = file.name.toLowerCase().split('.').pop();

  const audioExts = ['mp3', 'wav', 'm4a', 'aac', 'flac', 'ogg'];
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
  const videoExts = ['mp4', 'mov', 'avi', 'webm', 'mkv'];

  if (audioExts.includes(ext || '')) return 'audio';
  if (imageExts.includes(ext || '')) return 'image';
  if (videoExts.includes(ext || '')) return 'video';

  return 'unknown';
}
